// src/components/DistributeModal.jsx - 설문 배포 모달

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSearch, faCheck, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import './DistributeModal.scss';

const DistributeModal = ({ isOpen, onClose, surveyData, studyId, artifactId }) => {
  const [panels, setPanels] = useState([]);
  const [selectedPanels, setSelectedPanels] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterTags, setFilterTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);

  // 패널 목록 로드
  useEffect(() => {
    if (!isOpen) return;

    const loadPanels = async () => {
      setLoading(true);
      try {
        // 임시 더미 데이터
        setPanels([
          { id: 1, name: '홍길동', phone: '010-1234-5678', tags: ['20대', '여성', 'VIP'], status: 'active' },
          { id: 2, name: '김철수', phone: '010-2345-6789', tags: ['30대', '남성'], status: 'active' },
          { id: 3, name: '이영희', phone: '010-3456-7890', tags: ['20대', '여성'], status: 'active' },
        ]);
      } catch (error) {
        console.error('패널 로드 오류:', error);
      } finally {
        setLoading(false);
      }
    };

    loadPanels();
  }, [isOpen]);

  // 필터링된 패널 목록
  const filteredPanels = panels.filter(panel => {
    const matchesSearch = !searchTerm || 
      panel.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      panel.phone.includes(searchTerm);
    
    const matchesTags = filterTags.length === 0 ||
      filterTags.every(tag => panel.tags?.includes(tag));
    
    return matchesSearch && matchesTags && panel.status === 'active';
  });

  // 태그 목록 추출
  const allTags = Array.from(new Set(panels.flatMap(p => p.tags || [])));

  const handlePanelToggle = (panelId) => {
    setSelectedPanels(prev => 
      prev.includes(panelId)
        ? prev.filter(id => id !== panelId)
        : [...prev, panelId]
    );
  };

  const handleSelectAll = () => {
    if (selectedPanels.length === filteredPanels.length) {
      setSelectedPanels([]);
    } else {
      setSelectedPanels(filteredPanels.map(p => p.id));
    }
  };

  const handleTagToggle = (tag) => {
    setFilterTags(prev =>
      prev.includes(tag)
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleDistribute = async () => {
    if (selectedPanels.length === 0) {
      alert('배포할 패널을 선택해주세요.');
      return;
    }

    setSending(true);
    try {
      alert(`${selectedPanels.length}명에게 설문이 발송되었습니다.`);
      onClose();
    } catch (error) {
      console.error('배포 오류:', error);
      alert('배포 중 오류가 발생했습니다.');
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="distribute-modal-overlay">
      <div className="distribute-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="distribute-modal-header">
          <h2>설문 배포</h2>
          <button className="distribute-modal-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="distribute-modal-body">
          {/* 검색 및 필터 */}
          <div className="distribute-search-section">
            <div className="distribute-search-box">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="이름 또는 전화번호로 검색..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="distribute-search-input"
              />
            </div>
            
            <div className="distribute-tags-filter">
              <span className="filter-label">태그 필터:</span>
              <div className="tags-list">
                {allTags.map(tag => (
                  <button
                    key={tag}
                    className={`tag-filter-btn ${filterTags.includes(tag) ? 'active' : ''}`}
                    onClick={() => handleTagToggle(tag)}
                  >
                    {tag}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* 패널 목록 */}
          <div className="distribute-panels-section">
            <div className="distribute-panels-header">
              <h3>발송 대상 선택</h3>
              <button className="select-all-btn" onClick={handleSelectAll}>
                {selectedPanels.length === filteredPanels.length ? '전체 해제' : '전체 선택'}
              </button>
            </div>

            {loading ? (
              <div className="distribute-loading">로딩 중...</div>
            ) : (
              <div className="distribute-panels-list">
                {filteredPanels.length === 0 ? (
                  <div className="distribute-empty">선택 가능한 패널이 없습니다.</div>
                ) : (
                  filteredPanels.map(panel => (
                    <label key={panel.id} className="distribute-panel-item">
                      <input
                        type="checkbox"
                        checked={selectedPanels.includes(panel.id)}
                        onChange={() => handlePanelToggle(panel.id)}
                      />
                      <div className="panel-info">
                        <div className="panel-name">{panel.name}</div>
                        <div className="panel-details">
                          <span className="panel-phone">{panel.phone}</span>
                          {panel.tags && panel.tags.length > 0 && (
                            <div className="panel-tags">
                              {panel.tags.map(tag => (
                                <span key={tag} className="panel-tag">{tag}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </label>
                  ))
                )}
              </div>
            )}
          </div>

          {/* 선택 요약 */}
          <div className="distribute-summary">
            <span>선택된 패널: <strong>{selectedPanels.length}명</strong></span>
          </div>
        </div>

        <div className="distribute-modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            취소
          </button>
          <button 
            className="btn btn-primary" 
            onClick={handleDistribute}
            disabled={selectedPanels.length === 0 || sending}
          >
            <FontAwesomeIcon icon={faPaperPlane} />
            {sending ? '발송 중...' : '발송하기'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DistributeModal;
